import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowRight, UserPlus, X, ArrowLeft, Users } from "lucide-react";
import { useKioskIdleReturn } from "@/lib/useKioskIdleReturn";
import GenderField from "@/components/GenderField";
import { formatDisplayName } from "@/lib/names";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

const AGE_GROUPS = ["Child", "Youth", "Young Adult", "Adult", "Senior"];
const REFERRAL_SOURCES = ["Invited by friend", "Family", "Social media", "Web search", "Walked in", "Other"];

function Card({ children, testid }) {
  return (
    <div
      className="w-full max-w-md rounded-lg px-6 py-8 mx-auto"
      style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

function ConfirmedCard({ name, serviceName, extras, onDone }) {
  return (
    <Card testid="checkin-confirmed">
      <div className="flex flex-col items-center text-center">
        <div className="rounded-full p-4 mb-4" style={{ background: "rgba(168,164,110,0.15)" }}>
          <CheckCircle2 size={48} style={{ color: "var(--olive)" }} />
        </div>
        <h2 className="font-serif-display text-3xl mb-2" style={{ color: "var(--ink)" }}>You're checked in</h2>
        <p className="text-sm" style={{ color: "var(--ink-2)" }}>
          <strong>{name}</strong> — {serviceName}, today
        </p>
        {extras && extras.length > 0 && (
          <div className="mt-3 text-xs" style={{ color: "var(--ink-2)" }} data-testid="checkin-confirmed-extras">
            <div className="uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)", fontSize: "0.55rem" }}>Also checked in</div>
            <div>{extras.map((e) => formatDisplayName(e)).filter(Boolean).join(" • ")}</div>
          </div>
        )}
        <p className="text-xs mt-6 max-w-xs" style={{ color: "var(--muted)" }}>
          Grateful you're here. Have a blessed service.
        </p>
        <button onClick={onDone} className="btn-ghost mt-6 text-xs" data-testid="checkin-done">
          Check someone else in
        </button>
      </div>
    </Card>
  );
}

function FamilyStep({ code, guest, onSubmit, onSkip, busy }) {
  const [members, setMembers] = useState(null);
  const [selected, setSelected] = useState({});
  useEffect(() => {
    api.post(`/public/checkin/${code}/household`, { guest_id: guest.id })
      .then((r) => {
        setMembers(r.data.members || []);
        // Start un-checked — the guardian taps anyone actually here today.
        setSelected({});
      })
      .catch(() => setMembers([]));
  }, [code, guest.id]);

  if (members === null) {
    return (
      <Card testid="checkin-family-loading">
        <p className="text-sm text-center" style={{ color: "var(--muted)" }}>Looking up your family…</p>
      </Card>
    );
  }
  if (members.length === 0) {
    // No household members — just check the guardian in directly
    setTimeout(() => onSubmit([]), 0);
    return null;
  }
  const chosen = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  return (
    <Card testid="checkin-family">
      <div className="flex items-center gap-2 mb-3">
        <Users size={20} style={{ color: "var(--ember)" }} />
        <h2 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>Who's with you today?</h2>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--muted)" }}>
        Tap anyone in your family who's here with you. We'll check them in too.
      </p>
      <div className="space-y-2 mb-5">
        <div
          className="rounded-md flex items-center justify-between px-4"
          style={{ background: "rgba(180, 128, 78, 0.12)", border: "1px solid var(--ember)", minHeight: "56px" }}
        >
          <div className="text-lg font-medium" style={{ color: "var(--ink)" }}>
            {formatDisplayName(guest)} <span className="text-xs ml-1" style={{ color: "var(--muted)" }}>(you)</span>
          </div>
          <CheckCircle2 size={22} style={{ color: "var(--olive)" }} />
        </div>
        {members.map((m) => {
          const checked = !!selected[m.id];
          return (
            <label
              key={m.id}
              className="rounded-md flex items-center gap-3 cursor-pointer px-3 py-2 transition-colors"
              style={{
                background: checked ? "rgba(180, 128, 78, 0.12)" : "var(--bg-alt)",
                border: `1px solid ${checked ? "var(--ember)" : "var(--border-subtle)"}`,
                minHeight: "56px",
              }}
              data-testid={`checkin-family-member-${m.id}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setSelected({ ...selected, [m.id]: e.target.checked })}
                className="h-6 w-6 flex-shrink-0"
                style={{ accentColor: "var(--ember)" }}
                data-testid={`checkin-family-toggle-${m.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-lg font-medium truncate" style={{ color: "var(--ink)" }}>{formatDisplayName(m)}</div>
                {m.age_group && <div className="text-xs" style={{ color: "var(--muted)" }}>{m.age_group}</div>}
              </div>
              {m.is_guardian && <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ember)" }}>Guardian</span>}
            </label>
          );
        })}
      </div>
      <button
        onClick={() => onSubmit(chosen)}
        disabled={busy}
        className="btn-primary w-full"
        data-testid="checkin-family-submit"
      >
        {busy ? "Checking in…" : `Check in ${chosen.length + 1} ${chosen.length === 0 ? "person" : "people"}`}
        <ArrowRight size={14} />
      </button>
      <button onClick={onSkip} className="btn-ghost w-full mt-2 text-xs" data-testid="checkin-family-skip">
        Just me
      </button>
    </Card>
  );
}

function NewGuestForm({ code, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", gender: null, phone: "", email: "",
    address_city: "", age_group: "", referral_source: "QR check-in", invited_by: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!(form.first_name.trim() || form.last_name.trim())) { setErr("Please enter your name."); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/public/checkin/${code}/new-guest`, form);
      onSuccess(r.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong.");
    } finally { setBusy(false); }
  };

  const Field = ({ label, children }) => (
    <label className="block">
      <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>{label}</span>
      {children}
    </label>
  );

  return (
    <Card testid="checkin-new-guest-form">
      <button onClick={onCancel} className="flex items-center gap-1 text-xs mb-4" style={{ color: "var(--muted)" }} data-testid="checkin-new-guest-back">
        <ArrowLeft size={12} /> Back
      </button>
      <div className="overline mb-1" style={{ color: "var(--ember)", fontSize: "0.6rem" }}>Welcome</div>
      <h2 className="font-serif-display text-2xl mb-1" style={{ color: "var(--ink)" }}>First time?</h2>
      <p className="text-xs mb-6" style={{ color: "var(--muted)" }}>
        Let us know a little about you — takes a second and we'll follow up in a way that feels warm, not spammy.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name *">
            <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="checkin-first-name" />
          </Field>
          <Field label="Last name">
            <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="checkin-last-name" />
          </Field>
        </div>
        <Field label="Phone">
          <input inputMode="tel" placeholder="(555) 123-4567" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="checkin-phone" />
        </Field>
        <Field label="Email">
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="checkin-email" />
        </Field>
        <GenderField value={form.gender} onChange={(g) => setForm({ ...form, gender: g })} testid="checkin-gender" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="City">
            <input value={form.address_city} onChange={(e) => setForm({ ...form, address_city: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="checkin-city" />
          </Field>
          <Field label="Age group">
            <select value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="checkin-age">
              <option value="">—</option>
              {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
        </div>
        <Field label="How did you hear about us?">
          <select value={form.referral_source} onChange={(e) => setForm({ ...form, referral_source: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="checkin-referral">
            <option value="QR check-in">Walked in / scanned QR</option>
            {REFERRAL_SOURCES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Invited by (optional)">
          <input value={form.invited_by} onChange={(e) => setForm({ ...form, invited_by: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="checkin-invited-by" />
        </Field>
        {err && <p className="text-xs" style={{ color: "var(--terracotta)" }}>{err}</p>}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="checkin-new-guest-submit">
          {busy ? "Saving…" : "Check me in"} <ArrowRight size={14} />
        </button>
      </form>
    </Card>
  );
}

export default function Checkin() {
  useKioskIdleReturn();
  const { code } = useParams();
  const [landing, setLanding] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [last4, setLast4] = useState("");
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmed, setConfirmed] = useState(null); // {guest, service}
  const [showNewGuest, setShowNewGuest] = useState(false);
  const [familyFor, setFamilyFor] = useState(null); // guardian guest waiting for family selection

  useEffect(() => {
    api.get(`/public/checkin/${code}`)
      .then((r) => setLanding(r.data))
      .catch((e) => setLoadErr(e?.response?.data?.detail || "This QR isn't valid."));
  }, [code]);

  const reset = () => {
    setLast4(""); setMatches(null); setErr(""); setConfirmed(null); setShowNewGuest(false); setFamilyFor(null);
  };

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/public/checkin/${code}/lookup`, { last4 });
      setMatches(r.data.matches || []);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't look you up.");
    } finally { setBusy(false); }
  };

  const submitMark = async (guest, additionalIds) => {
    setBusy(true);
    try {
      const r = await api.post(`/public/checkin/${code}/mark`, { guest_id: guest.id, additional_guest_ids: additionalIds || [] });
      setConfirmed({
        name: formatDisplayName(r.data.guest),
        serviceName: r.data.service.name,
        already: r.data.already_checked_in,
        extras: r.data.additional_marked || [],
      });
      setFamilyFor(null);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't mark you present.");
    } finally { setBusy(false); }
  };

  const pickMatch = (match) => {
    // Guardians go through the family selection step; everyone else checks in directly.
    if (match.is_household_guardian) {
      setFamilyFor(match);
    } else {
      submitMark(match, []);
    }
  };

  if (loadErr) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--bg-base)" }}>
        <Card testid="checkin-invalid">
          <div className="text-center">
            <div className="rounded-full p-4 mb-4 inline-block" style={{ background: "rgba(191,90,66,0.15)" }}>
              <X size={40} style={{ color: "var(--terracotta)" }} />
            </div>
            <h2 className="font-serif-display text-2xl mb-2" style={{ color: "var(--ink)" }}>QR not recognized</h2>
            <p className="text-sm" style={{ color: "var(--muted)" }}>{loadErr}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!landing) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-base)" }}><p style={{ color: "var(--muted)" }}>Loading…</p></div>;
  }

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: "var(--bg-base)" }}>
      <div className="max-w-md mx-auto mb-6 text-center">
        <div className="overline mb-1" style={{ color: "var(--ember)", fontSize: "0.65rem" }}>{landing.organization.name}</div>
        {landing.service ? (
          <>
            <h1 className="font-serif-display text-3xl mb-1" style={{ color: "var(--ink)" }}>{landing.service.name}</h1>
            <p className="text-xs" style={{ color: "var(--muted)" }}>
              {landing.service_start_ct ? <>Service {landing.service_start_ct}</> : <>Check-in for {landing.today}</>}
            </p>
          </>
        ) : (
          <h1 className="font-serif-display text-3xl mb-1" style={{ color: "var(--ink)" }}>Check-in</h1>
        )}
      </div>

      {landing.window_status && landing.window_status !== "open" && !confirmed ? (
        <Card testid="checkin-window-closed">
          <div className="text-center">
            <div className="rounded-full p-4 mb-4 inline-block" style={{ background: "rgba(191,90,66,0.12)" }}>
              <X size={36} style={{ color: "var(--terracotta)" }} />
            </div>
            <h2 className="font-serif-display text-2xl mb-2" style={{ color: "var(--ink)" }}>
              {landing.window_status === "upcoming" ? "Check-in isn't open yet" :
               landing.window_status === "closed"   ? "Check-in has closed" :
                                                      "No service scheduled"}
            </h2>
            {landing.service ? (
              <p className="text-sm max-w-xs mx-auto" style={{ color: "var(--muted)" }} data-testid="checkin-window-msg">
                {landing.window_status === "upcoming" ? (
                  <>Check-in for <strong style={{ color: "var(--ink-2)" }}>{landing.service.name}</strong> opens at <strong style={{ color: "var(--ink-2)" }}>{landing.window_opens_ct}</strong>. Come back then and we'll be ready for you.</>
                ) : (
                  <>Check-in for <strong style={{ color: "var(--ink-2)" }}>{landing.service.name}</strong> closed at <strong style={{ color: "var(--ink-2)" }}>{landing.window_closes_ct}</strong>. If you're just joining us, tell a leader — they'll help you out.</>
                )}
              </p>
            ) : (
              <p className="text-sm max-w-xs mx-auto" style={{ color: "var(--muted)" }}>Your church hasn't added a service yet. Ask your leader.</p>
            )}
            <p className="text-[0.65rem] mt-6" style={{ color: "var(--muted)" }}>Now: {landing.now_ct}</p>
          </div>
        </Card>
      ) : confirmed ? (
        <ConfirmedCard name={confirmed.name} serviceName={landing.service?.name || ""} extras={confirmed.extras} onDone={reset} />
      ) : familyFor ? (
        <FamilyStep
          code={code}
          guest={familyFor}
          busy={busy}
          onSubmit={(ids) => submitMark(familyFor, ids)}
          onSkip={() => submitMark(familyFor, [])}
        />
      ) : showNewGuest ? (
        <NewGuestForm code={code} onSuccess={(d) => setConfirmed({ name: formatDisplayName(d.guest), serviceName: d.service.name })} onCancel={() => setShowNewGuest(false)} />
      ) : matches === null ? (
        <Card testid="checkin-lookup">
          <h2 className="font-serif-display text-xl mb-2" style={{ color: "var(--ink)" }}>Mark yourself present</h2>
          <p className="text-xs mb-6" style={{ color: "var(--muted)" }}>Enter the last 4 digits of your phone number.</p>
          <form onSubmit={lookup} className="space-y-4">
            <input
              autoFocus
              inputMode="numeric"
              maxLength={4}
              placeholder="1234"
              value={last4}
              onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="w-full px-4 py-4 text-2xl text-center tracking-[0.4em] font-serif-display"
              data-testid="checkin-last4"
            />
            {err && <p className="text-xs text-center" style={{ color: "var(--terracotta)" }}>{err}</p>}
            <button type="submit" disabled={busy || last4.length < 4} className="btn-primary w-full" data-testid="checkin-lookup-submit">
              {busy ? "Searching…" : "Find me"} <ArrowRight size={14} />
            </button>
          </form>
          <div className="mt-6 pt-6 border-t text-center" style={{ borderColor: "var(--border-subtle)" }}>
            <p className="text-xs mb-2" style={{ color: "var(--muted)" }}>First time here?</p>
            <button onClick={() => setShowNewGuest(true)} className="btn-ghost text-xs" data-testid="checkin-new-guest-btn">
              <UserPlus size={13} /> Create a profile
            </button>
          </div>
        </Card>
      ) : (
        <Card testid="checkin-matches">
          <button onClick={reset} className="flex items-center gap-1 text-xs mb-4" style={{ color: "var(--muted)" }} data-testid="checkin-back">
            <ArrowLeft size={12} /> Try a different number
          </button>
          <h2 className="font-serif-display text-xl mb-2" style={{ color: "var(--ink)" }}>
            {matches.length === 0 ? "No match" : matches.length === 1 ? "Is this you?" : "Which one is you?"}
          </h2>
          {matches.length === 0 ? (
            <>
              <p className="text-sm mb-4" style={{ color: "var(--muted)" }}>
                We didn't find anyone with a phone number ending in <strong>{last4}</strong>.
              </p>
              <button onClick={() => setShowNewGuest(true)} className="btn-primary w-full" data-testid="checkin-goto-new-guest">
                <UserPlus size={14} /> I'm new — create a profile
              </button>
            </>
          ) : (
            <div className="space-y-2">
              {matches.map((m) => (
                <button
                  key={m.id}
                  onClick={() => pickMatch(m)}
                  disabled={busy}
                  className="w-full text-left p-4 rounded flex items-center justify-between gap-3 hover:bg-[var(--surface-hover)] transition-colors"
                  style={{ background: "var(--surface-hover)" }}
                  data-testid={`checkin-match-${m.id}`}
                >
                  <div>
                    <div className="font-medium text-sm" style={{ color: "var(--ink)" }}>{formatDisplayName(m)}</div>
                    <div className="text-[0.65rem]" style={{ color: "var(--muted)" }}>
                      {m.phone_hint}{m.is_household_guardian ? " · Guardian" : ""}
                    </div>
                  </div>
                  {m.is_household_guardian ? <Users size={20} style={{ color: "var(--ember)" }} /> : <CheckCircle2 size={20} style={{ color: "var(--olive)" }} />}
                </button>
              ))}
              <div className="mt-3 pt-3 border-t text-center" style={{ borderColor: "var(--border-subtle)" }}>
                <p className="text-xs mb-1" style={{ color: "var(--muted)" }}>Not you?</p>
                <button onClick={() => setShowNewGuest(true)} className="btn-ghost text-xs" data-testid="checkin-alt-new-guest">
                  <UserPlus size={13} /> Create a new profile
                </button>
              </div>
            </div>
          )}
          {err && <p className="text-xs mt-3" style={{ color: "var(--terracotta)" }}>{err}</p>}
        </Card>
      )}
    </div>
  );
}
